import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Bell, Plus, Repeat } from "lucide-react";

export function AssetAlertDialog({ open, onOpenChange, defaultPrice, onCreate, hideTrigger = false }) {
  const [condition, setCondition] = useState("above");
  const [targetPrice, setTargetPrice] = useState(defaultPrice || "");
  const [repeat, setRepeat] = useState(false);

  useEffect(() => {
    if (defaultPrice && !targetPrice) setTargetPrice(defaultPrice);
  }, [defaultPrice, targetPrice]);

  const handleSubmit = async () => {
    const ok = await onCreate(condition, targetPrice, repeat);
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button
            data-testid="create-alert-btn"
            variant="outline"
            className="border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#F59E0B]/10 font-semibold"
          >
            <Bell className="w-4 h-4 mr-2" />
            Set Alert
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="glass-solid">
        <DialogHeader>
          <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Unbounded' }}>
            Create Price Alert
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            We'll email you the moment the price crosses your target
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label className="text-gray-400">Condition</Label>
            <Select value={condition} onValueChange={setCondition}>
              <SelectTrigger data-testid="alert-condition-select" className="bg-black/20 border-white/10 text-white mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0B0F19] border-white/10">
                <SelectItem value="above">Price goes above</SelectItem>
                <SelectItem value="below">Price goes below</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-400">Target Price</Label>
            <Input
              data-testid="alert-price-input"
              type="number"
              value={targetPrice}
              onChange={(e) => setTargetPrice(e.target.value)}
              placeholder="0.00"
              className="bg-black/20 border-white/10 text-white mt-2"
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-white/10 bg-black/20 px-3 py-2.5">
            <div className="flex items-center gap-2">
              <Repeat className="w-3.5 h-3.5 text-[#00E0FF]" />
              <div>
                <div className="text-xs font-semibold text-white">Repeat alert</div>
                <div className="text-[10px] text-gray-500">Re-arms after each trigger — notifies on every cross</div>
              </div>
            </div>
            <Switch data-testid="alert-repeat-switch" checked={repeat} onCheckedChange={setRepeat} />
          </div>
          <Button
            data-testid="confirm-alert-btn"
            onClick={handleSubmit}
            className="w-full bg-[#F59E0B] text-black hover:bg-[#F59E0B]/90 font-semibold"
          >
            Create Alert
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AssetPortfolioDialog({ open, onOpenChange, defaultPrice, onAdd }) {
  const [quantity, setQuantity] = useState("");
  const [buyPrice, setBuyPrice] = useState(defaultPrice || "");

  useEffect(() => {
    if (defaultPrice && !buyPrice) setBuyPrice(defaultPrice);
  }, [defaultPrice, buyPrice]);

  const handleSubmit = async () => {
    const ok = await onAdd(quantity, buyPrice);
    if (ok) {
      onOpenChange(false);
      setQuantity("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          data-testid="add-to-portfolio-btn"
          className="bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-semibold shadow-[0_0_20px_rgba(0,224,255,0.3)] hover:shadow-[0_0_30px_rgba(0,224,255,0.5)] transition-all duration-300"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add to Portfolio
        </Button>
      </DialogTrigger>
      <DialogContent className="glass-solid">
        <DialogHeader>
          <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Unbounded' }}>
            Add to Portfolio
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Track this asset's performance in your portfolio
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label className="text-gray-400">Quantity</Label>
            <Input
              data-testid="portfolio-quantity-input"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="0.00"
              className="bg-black/20 border-white/10 text-white mt-2"
            />
          </div>
          <div>
            <Label className="text-gray-400">Buy Price</Label>
            <Input
              data-testid="portfolio-price-input"
              type="number"
              value={buyPrice}
              onChange={(e) => setBuyPrice(e.target.value)}
              placeholder="0.00"
              className="bg-black/20 border-white/10 text-white mt-2"
            />
          </div>
          <Button
            data-testid="confirm-add-portfolio-btn"
            onClick={handleSubmit}
            className="w-full bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-semibold"
          >
            Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
